import { inject, Injectable } from '@angular/core';
import { Environment, EnvironmentService } from '@galaxy/core';
import { TokenStatus } from '@vendasta/facebook';
import { InstagramApiService } from '@vendasta/instagram';
import { firstValueFrom, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { PARTNER_ID_TOKEN } from '../../../../core/src/lib/tokens';
import { SocialPageInstagram } from '../interfaces';

// TODO(BREW-687): update this interface according to instagram spec
export interface InstagramPageConnectionInfo {
  name: string;
  profileUrl: string;
  tokenStatus: TokenStatus;
  messengerEnabled: boolean;
  isAssignedPageOwner: boolean;
  statusCheckPermissionError?: boolean;
}

@Injectable({
  providedIn: 'any',
})
export class InstagramMessagesService {
  private readonly instagramAPIService = inject(InstagramApiService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly partnerID$ = inject(PARTNER_ID_TOKEN);

  enableMessaging(instagramPageData: SocialPageInstagram): Observable<unknown> {
    return this.instagramAPIService.enableMessaging({
      partnerId: instagramPageData.partnerId,
      businessId: instagramPageData.businessId,
      userId: instagramPageData.internalInstagramUserId,
    });
  }

  async redirectToFacebookAuth(accountID: string): Promise<void> {
    const protocol = window.location.protocol;
    const host = window.location.host;
    const baseUrl = `${protocol}//${host}`;
    const nextUrl: URL = new URL(baseUrl);
    nextUrl.pathname = `/account/location/${accountID}/settings/connections/target/inbox`;

    const partnerID = await firstValueFrom(this.partnerID$);
    const env = this.environmentService.getEnvironment() === Environment.PROD ? 'prod' : 'demo';
    window.location.href = `https://instagram-${env}.apigateway.co/v2/auth-user?partnerID=${partnerID}&businessID=${accountID}&nextURL=${nextUrl.href}`;
  }

  disableMessaging(instagramPageData: SocialPageInstagram): Observable<unknown> {
    return this.instagramAPIService.disableMessaging({ instagramUserId: instagramPageData.instagramUserId });
  }

  getPagesForAccount(accountGroupId: string): Observable<SocialPageInstagram[]> {
    return this.partnerID$.pipe(
      switchMap((partnerID) => {
        return this.instagramAPIService.listUsers({
          partnerId: partnerID,
          businessId: accountGroupId,
          options: {
            pageSize: 100,
          },
        });
      }),
      map((response) => {
        return response.users.map((user): SocialPageInstagram => {
          return {
            type: 'instagram',
            name: user.fullName,
            username: user.username,
            facebookPageId: user.facebookUserId,
            instagramUserId: user.instagramUserId,
            internalInstagramUserId: user.userId,
            partnerId: user.partnerId,
            businessId: user.businessId,
            profileUrl: `https://www.instagram.com/${user.username}/`,
            // TODO(BREW-687): Can we populate any of these? Are any relevant?
            isVerified: true,
            locationId: '',
            mapsUri: '',
            ssid: '',
            pages: [],
            clientTags: [],
            deleteUrl: '',
            tokenIsBroken: false,
          };
        });
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getConnectionState(
    instagramPageData: SocialPageInstagram,
    _partnerID: string,
    _accountGroupID: string,
  ): Observable<InstagramPageConnectionInfo> {
    // const enabledForPage$ = this.fbAPIService.facebookPageMessengerState({
    //   organizationId: partnerID + ':' + accountGroupID,
    //   pageId: facebookPageData.profileUrl?.split('id=')[1],
    //   facebookUserId: facebookPageData.facebookUserID,
    // });

    // return enabledForPage$.pipe(
    //   map((enabledForPage) => {
    //     return {
    //       name: facebookPageData.name,
    //       tokenStatus: TokenStatus.VALID,
    //       messengerEnabled: !!enabledForPage.messengerEnabled,
    //       isAssignedPageOwner: !!enabledForPage.isPageOwner,
    //     };
    //   }),
    //   catchError((e: HttpErrorResponse) => {
    //     const statusCheckPermissionError = e.status === 403;
    //     return of({
    //       name: facebookPageData.name,
    //       tokenStatus: TokenStatus.UNKNOWN,
    //       messengerEnabled: false,
    //       isAssignedPageOwner: false,
    //       statusCheckPermissionError,
    //     });
    //   }),
    // );
    return of({
      name: instagramPageData.name,
      profileUrl: instagramPageData.profileUrl,
      tokenStatus: TokenStatus.VALID,
      messengerEnabled: false,
      isAssignedPageOwner: true,
    });
  }
}
